import React, { useEffect, useState, useCallback } from 'react';
import { Button, message, Typography, Select, Empty, Modal, Form, Input, Tooltip, Popconfirm, Dropdown } from 'antd';
import { ReloadOutlined, PlusOutlined } from '@ant-design/icons';
import { filmApi } from '../../services/api/film';
import { apiErrorText, bankInUse } from './apiErrorText';
import { identityErrorText } from './characterIdentityUtils';
import { useFilmPlan } from './FilmPlanContext';
import EpisodeOnlyFilter from './EpisodeOnlyFilter';
import { useBankUsage } from './useBankUsage';
import { useBankErrorReporter } from './WorkflowRunContext'; // P9D #6 (I10)
import { filterByEpisode } from './bankFilterUtils';
import FilmCharacterCard from './FilmCharacterCard';
import { looksOf, characterVideoCountOf } from './characterCardUtils';
import { lookLabelOf } from './assetCardsUi';
import { lookUsageNote, type LookUsageMap } from './sceneStripUtils';
import { looksNeedingImageDetail } from './bankPrepUtils';
import FaceSourceModal from './FaceSourceModal';
import { regenConfirmText, staleRegenPlan, sheetModelOptions } from './faceSourceUtils';
import { lookAssetAction } from './lookAssetUtils';
import { needsAvisBatch, refAssetView } from './refAssetUtils';
import ImageComposer, { type RefImage } from './ImageComposer';
import { useBankVars } from './useBankVars';
import './bank.css';
import type { CharacterAppearance, FilmCharacterIdentityInput, FilmCharacter, FilmCharacterLook, FilmRefAsset, FilmSceneLook, ImageModelInfo, CastCandidate, CastAppearance, CastKind, FilmLocation, FilmProp } from '../../types/film';
import FilmCrowdCard from './FilmCrowdCard';
import { addDismissed, clearDismissed, readDismissed } from './castDismiss';
import { AssetTabHead, AssetAddButton, AssetHeadButton } from './AssetTabHead'; // UI-assettabs
import { ASSET_TAB_HEAD } from './assetCardsUi';
import './assetCards.css';

const { Text, Paragraph } = Typography;

/**
 * Giới của nhân vật — NEO GIỌNG voice-over (260811).
 *
 * Không phải thông tin trang trí: mệnh đề giới trong prompt là thứ DUY NHẤT chọn được giọng V.O. của engine.
 * Thiếu nó, engine bốc giọng theo khuôn mặt rõ nhất trong khung; khung không có mặt thì mặc định GIỌNG NAM
 * (đo prod 10/08: 81.2% ca V.O. mất neo, nữ chính Violet ra giọng nam).
 * Để trống là hợp lệ và AN TOÀN HƠN đoán bừa: sai giới ép engine đọc sai chắc chắn, còn bỏ trống thì nó
 * vẫn bám mặt trong khung.
 */
const GENDER_OPTIONS = [
  { value: 'female', label: 'Female' },
  { value: 'male', label: 'Male' },
];

/** `onChanged` báo drawer tải lại dòng "Chuẩn bị x/3" (260922 E2-P3). */
/** `onlyIds` (261030 B1): ngăn Sửa của màn Tài sản khung 4 bước chỉ mở ĐÚNG nhân vật/đám đông này — giữ nguyên mọi nút sửa ảnh, ẩn đầu tab / danh sách rà ngược / bộ lọc tập. */
const FilmReferenceTab: React.FC<{ onChanged?: () => void; workflowPlanId?: string; compact?: boolean; onlyIds?: string[] }> = ({ onChanged, workflowPlanId, compact, onlyIds }) => {
  const scoped = !!onlyIds;
  const { storyId, planId, plan } = useFilmPlan();
  const reportError = useBankErrorReporter();
  const vars = useBankVars();
  const isSeedance = plan?.videoProvider === 'SEEDANCE';
  const [chars, setChars] = useState<FilmCharacter[]>([]);
  // 260819: lọc theo tập đang mở. Nhân vật lấy từ ba nguồn (shot · bảng gán trang phục · cast của plan) —
  // thiếu một người ở đây là người vận hành không gán được mặt cho họ, nên thà thừa còn hơn thiếu.
  const { usage, reload: reloadUsage,error:usageError } = useBankUsage(planId,!!workflowPlanId);
  const [epOnly, setEpOnly] = useState(true);
  const [voices, setVoices] = useState<Array<{ id: string; label: string; gender: string }>>([]);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState<Record<string, string>>({}); // charId -> action
  // 260922 E2-P1: thẻ mới cần bộ đồ (để hiện tab + ảnh).
  const [looks, setLooks] = useState<FilmCharacterLook[]>([]);
  const [genLookIds, setGenLookIds] = useState<Set<string>>(new Set());
  // 260922 E2-P2: gán bộ đồ theo cảnh của plan đang mở + nơi dùng bộ đồ toàn truyện (cho dòng nhắc).
  const [sceneRows, setSceneRows] = useState<FilmSceneLook[]>([]);
  const [lookUsage, setLookUsage] = useState<LookUsageMap | null>(null);
  const [replacing, setReplacing] = useState<{ look: FilmCharacterLook; usage: string[]; name: string; to?: string } | null>(null);
  // `wardrobe` (trang phục gốc AI khai — nguồn tạo bộ `default` khi chưa có bộ nào) sửa qua menu ⋯ của thẻ.
  const [wardrobeEditing, setWardrobeEditing] = useState<{ c: FilmCharacter; value: string } | null>(null);
  // 260922 E2-P3: nhận nốt việc của tab Wardrobe (đã gỡ) — Xưởng ảnh ma-nơ-canh, gen hàng loạt bộ còn thiếu.
  const [outfitStudio, setOutfitStudio] = useState<FilmCharacterLook | null>(null);
  // 260927 (mục A/B) — ô soạn Custom Prompt cho ảnh NGƯỜI MẶC (sheet). `lockedRef` = ảnh bộ chính khoá khi
  // đang soạn cho một bộ PHỤ (Add Look — TopView "Image 1 is the default outfit, locked").
  const [sheetComposer, setSheetComposer] = useState<{ look: FilmCharacterLook; lockedRef?: RefImage } | null>(null);
  const openSheetComposer = (c: FilmCharacter, look: FilmCharacterLook) => {
    const mine = looksOf(c.id, looks);
    const main = mine.find((l) => l.lookKey === 'default') || mine[0] || null;
    const lockedRef = (main && main.id !== look.id && main.canonicalRefUrl)
      ? { key: main.id, url: main.canonicalRefUrl, label: `Ảnh bộ chính (${main.lookKey}) — giữ mặt; gỡ để dựng từ chữ` }
      : undefined;
    setSheetComposer({ look, lockedRef });
  };
  const [batch, setBatch] = useState<string | null>(null);
  // 260922 E3-P3 — sổ asset theo nhà cung cấp (AVIS KYC).
  const [refError,setRefError]=useState<string|null>(null);
  const [refRows, setRefRows] = useState<FilmRefAsset[]>([]);
  const [avisBatch, setAvisBatch] = useState<string | null>(null);
  // `label` + `prefix` (260922 E3-P6): dòng treo có thể của bộ đồ (`lk-…`) hoặc của ảnh nhóm đám đông (`cr-…`).
  const [reconciling, setReconciling] = useState<{ rowId: string; label: string; prefix: string; assetId: string; busy: boolean } | null>(null);
  // 260922 E1-P2: rà ngược "event nhắc ai mà Bank chưa có" + nơi xuất hiện của đám đông / vai lặp đã có hồ sơ.
  const [candidates, setCandidates] = useState<CastCandidate[]>([]);
  const [appearances, setAppearances] = useState<Record<string, CastAppearance>>({});
  const [dismissed, setDismissed] = useState<string[]>(() => readDismissed(storyId));
  const [candKind, setCandKind] = useState<Record<string, CastKind>>({});
  const [candBusy, setCandBusy] = useState<string | null>(null);
  // Thêm/xoá nhân vật BẰNG TAY (260806, user báo): "Promote cast" chỉ lấy được người AI nhận ra; người AI bỏ
  // sót thì trước đây không có đường thêm, và bản dư (tên rác / trùng) thì không xoá được.
  const [adding, setAdding] = useState(false);
  const [addForm] = Form.useForm();
  // 260922 E3-P5 — hộp *Mặt nhân vật* (thay modal Digital Character): thư viện avatar · tải ảnh mặt · gen từ mô tả.
  const [seedanceEditing, setSeedanceEditing] = useState<FilmCharacter | null>(null);
  // Danh mục model gen ảnh — ô *Gen lại ▾* của ảnh bộ đồ lọc theo cờ đo (E3-P5).
  const [imageModels, setImageModels] = useState<ImageModelInfo[]>([]);
  // 260927 (mục 3, review) — "＋ Ref → Chọn ảnh trong Bank" của ô soạn nhân vật: tab Bối cảnh/Đạo cụ đã có
  // (chỉ ảnh CÙNG loại), tab Nhân vật thì chưa truyền `bankImages` gì cả. Nạp locs/props CHỈ để dựng danh
  // sách chọn — không lọc theo tập (giữ đơn giản, giống cách imageModels nạp riêng ở trên).
  const [locs, setLocs] = useState<FilmLocation[]>([]);
  const [props_, setProps] = useState<FilmProp[]>([]);
  useEffect(() => {
    filmApi.listLocations(storyId).then(setLocs).catch(() => setLocs([]));
    filmApi.listProps(storyId).then(setProps).catch(() => setProps([]));
  }, [storyId]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [c, l] = await Promise.all([filmApi.listCharacters(storyId), filmApi.listLooks(storyId)]);
      setChars(c); setLooks(l);
    }
    catch (e: any) { message.error(`Failed to load: ${e?.message}`); }
    finally { setLoading(false); }
    filmApi.lookUsage(storyId).then(setLookUsage).catch(() => setLookUsage(null));
    filmApi.refAssets(storyId).then(r=>{setRefRows(r);setRefError(null);}).catch(()=>{if(workflowPlanId)setRefError('Không đọc được sổ AVIS; chưa biết trạng thái đăng ký.');else setRefRows([]);});
    if (planId) filmApi.sceneLooks(planId).then(setSceneRows).catch(() => setSceneRows([]));
    else setSceneRows([]);
    // Rà ngược hỏng thì khối đó ẩn — không làm hỏng cả tab.
    if (planId) {
      filmApi.castCandidates(planId)
        .then((r) => { setCandidates(r.candidates || []); setAppearances(r.appearances || {}); })
        .catch(() => { setCandidates([]); setAppearances({}); });
    } else { setCandidates([]); setAppearances({}); }
  }, [storyId, planId]);

  useEffect(() => { load(); filmApi.listVoices().then(setVoices).catch(() => {}); }, [load]);
  useEffect(() => { filmApi.imageStudioModels().then(setImageModels).catch(() => setImageModels([])); }, []);

  // Nạp lại cả bộ lọc theo tập: hồ sơ vừa thêm (vd đám đông từ danh sách rà ngược) mới lọt qua "Chỉ tập này".
  const changed = () => { load(); reloadUsage(); onChanged?.(); };
  const autoVoices = async () => {
    try { await filmApi.autoAssignVoices(storyId); message.success('Auto-assigned voices by gender'); load(); }
    catch (e: any) { message.error(`Error: ${e?.message}`); }
  };
  const setVoice = async (charId: string, voiceId: string) => {
    try { await filmApi.updateCharacter(charId, { voiceId }); load(); }
    catch (e: any) { message.error(`Error: ${e?.message}`); }
  };

  const addChar = async () => {
    const v = await addForm.validateFields();
    try {
      const saved = await (workflowPlanId?filmApi.createWorkflowCharacter(workflowPlanId,v):filmApi.createCharacter(storyId,v));
      // Tên có thể KHÁC cái vừa gõ: server hợp nhất "Elysia" vào "Elysia Featherfall" đã có (chống nhân bản).
      message.success(saved.name.toLowerCase() === String(v.name).trim().toLowerCase()
        ? `Đã thêm "${saved.name}"`
        : `Đã gộp vào nhân vật có sẵn "${saved.name}" (tên bạn gõ thành alias)`);
      setAdding(false); addForm.resetFields(); changed();
    } catch (e: any) { message.error(apiErrorText(e, 'Thêm nhân vật')); }
  };

  /** Xoá: 409 khi nhân vật đang được dùng (đã gán mặt / có look / xuất hiện trong event) → hỏi lại rồi cưỡng chế. */
  const delChar = async (c: FilmCharacter, force = false) => {
    setBusy((b) => ({ ...b, [c.id]: 'delete' }));
    try {
      const r = await filmApi.deleteCharacter(c.id, force);
      message.success(`Đã xoá "${r.name}"${r.looksDeleted ? ` + ${r.looksDeleted} look` : ''}`);
      changed();
    } catch (e: any) {
      const inUse = bankInUse(e);
      if (!inUse) { message.error(apiErrorText(e, 'Xoá nhân vật')); return; }
      Modal.confirm({
        title: `"${inUse.name}" đang được dùng`,
        content: <div style={{ fontSize: 13 }}>{inUse.usage.map((u) => <div key={u}>• {u}</div>)}<div style={{ marginTop: 8 }}>Vẫn xoá? Không hoàn lại được.</div></div>,
        okText: 'Vẫn xoá', okButtonProps: { danger: true }, cancelText: 'Huỷ',
        onOk: () => delChar(c, true),
      });
    } finally { setBusy((b) => { const n = { ...b }; delete n[c.id]; return n; }); }
  };

  const run = async (charId: string, action: 'ref' | 'element') => {
    setBusy((b) => ({ ...b, [charId]: action }));
    try {
      if (action === 'ref') {
        message.loading({ content: 'Generating canonical ref (Gemini)...', key: charId, duration: 0 });
        await filmApi.generateCharacterRef(charId);
        message.success({ content: 'Ref generated', key: charId });
      } else {
        await filmApi.createCharacterElement(charId);
        message.success('Kling element creation queued (element_id resolves later)');
      }
      load();
    } catch (e: any) { message.error({ content: `Error: ${e?.response?.data?.message || e?.message}`, key: charId }); }
    finally { setBusy((b) => { const n = { ...b }; delete n[charId]; return n; }); }
  };

  // ── 260922 E2-P1: sửa tại chỗ trên thẻ (thay modal Edit) ──
  const patchChar = async (c: FilmCharacter, data: Parameters<typeof filmApi.updateCharacter>[1], ctx: string) => {
    try {
      const saved = await (workflowPlanId&&c.kind==='CROWD'?filmApi.updateWorkflowCrowd(workflowPlanId,c.id,data):filmApi.updateCharacter(c.id,data));
      setChars((list) => list.map((x) => (x.id === c.id ? { ...x, ...saved } : x)));
      onChanged?.();
    } catch (e: any) { reportError(e, ctx); }
  };
  const saveAppearance = (c: FilmCharacter, appearance: CharacterAppearance | null) =>
    patchChar(c, { appearance }, 'Lưu ngoại hình');
  const saveField = (c: FilmCharacter, field: 'name' | 'role', value: string) =>
    patchChar(c, { [field]: value }, field === 'name' ? 'Đổi tên' : 'Đổi vai');
  const saveGender = (c: FilmCharacter, gender: string | null) =>
    patchChar(c, { gender: gender || '' }, 'Đổi giới');
  /**
   * 261010 P10 (S1) — ô «Dựng ảnh từ nhân vật». `identity: null` = bỏ liên kết; object = đặt/đổi. Đổi liên kết ⇒ BE đánh ảnh của nhân vật «cũ» ⇒ nạp lại cả nhân vật lẫn bộ đồ
   * (`changed`) để badge «cũ» + nút tạo lại hiện ngay. Lỗi mã IDENTITY_xxx hoặc BAD_FIELDS ⇒ câu tiếng Việt, form giữ nháp (trả `false`).
   */
  const saveIdentity = async (c: FilmCharacter, identity: FilmCharacterIdentityInput | null): Promise<boolean> => {
    const what = 'Lưu «Dựng ảnh từ nhân vật»';
    try {
      await filmApi.updateCharacter(c.id, { identity });
      changed();
      return true;
    } catch (e: any) {
      reportError(e, what, (err) => message.error(identityErrorText(err) ?? apiErrorText(err, what)));
      return false;
    }
  };
  const saveLookOutfit = async (look: FilmCharacterLook, outfit: string) => {
    try {
      const saved = await filmApi.updateLook(look.id, { outfit });
      setLooks((list) => list.map((x) => (x.id === look.id ? { ...x, ...saved } : x)));
    } catch (e: any) { message.error(apiErrorText(e, 'Lưu mô tả bộ đồ')); }
  };
  /**
   * Gen ảnh NGƯỜI MẶC bộ đồ = ảnh 16:9 2K, 1 cận + 3 góc (261008 E1-P2; 260930 từng 2 khung 4:3, 260922 E3-P1 sheet 4 khung). Kết quả cho nút gen hàng loạt:
   * `refused` = server từ chối CÓ CHỦ ĐÍCH (400: đám đông · bộ phụ khi bộ chính chưa ảnh) ⇒ bỏ qua bộ đó, chạy tiếp;
   * `error` = lỗi thật (kiểm duyệt / quota / mạng) ⇒ dừng cả lô.
   */
  const genLook = async (
    look: FilmCharacterLook, model?: string | null,
    // 260927 (mục A/B) — ô soạn Custom Prompt: prompt sửa tay + ảnh tham chiếu thêm.
    composerOpts?: { promptOverride?: string | null; refImages?: string[] },
  ): Promise<'ok' | 'refused' | 'error'> => {
    setGenLookIds((s) => new Set(s).add(look.id));
    try {
      const saved = await filmApi.generateLookRef(look.id, { planId, model, ...composerOpts });
      setLooks((list) => list.map((x) => (x.id === look.id ? { ...x, ...saved } : x)));
      // E3-P5: ảnh mới có thể làm bộ khác thành "dựng từ mặt cũ" (bộ chính đổi) ⇒ nạp lại cờ do server tính.
      filmApi.listLooks(storyId).then(setLooks).catch(() => undefined);
      reloadRefs(); // ảnh mới ⇒ server đã tự đăng ký AVIS (E3-P3)
      const label = model ? imageModels.find((m) => m.id === model)?.label : null;
      message.success(`Đã gen ảnh bộ "${look.lookKey}"${label ? ` bằng ${label}` : ''}`);
      (saved.warnings || []).forEach((w) => message.warning(w));
      onChanged?.();
      return 'ok';
    } catch (e: any) {
      // 260927 mục N7 — câu lỗi "bị kiểm duyệt" + gợi ý model/mô tả (server dựng sẵn) có thể dài — 8s.
      reportError(e, `Gen ảnh bộ "${look.lookKey}"`, (err) => message.error({ content: apiErrorText(err, `Gen ảnh bộ "${look.lookKey}"`), duration: 8 }));
      return e?.response?.status === 400 ? 'refused' : 'error';
    }
    finally { setGenLookIds((s) => { const n = new Set(s); n.delete(look.id); return n; }); }
  };
  /** 260927 (mục B.1) — "Tải ảnh lên" thay THẲNG ảnh người mặc, không qua AI. */
  const uploadLookRef = async (look: FilmCharacterLook, file: File) => {
    setGenLookIds((s) => new Set(s).add(look.id));
    try {
      const saved = await filmApi.uploadLookRef(look.id, file, planId);
      setLooks((list) => list.map((x) => (x.id === look.id ? { ...x, ...saved } : x)));
      reloadRefs();
      message.success(`Đã tải ảnh người mặc cho bộ "${look.lookKey}"`);
      (saved.warnings || []).forEach((w) => message.warning(w));
      onChanged?.();
    } catch (e: any) { reportError(e, `Tải ảnh bộ "${look.lookKey}"`); }
    finally { setGenLookIds((s) => { const n = new Set(s); n.delete(look.id); return n; }); }
  };
  /**
   * E3-P5 — gen lại các bộ "dựng từ mặt cũ" của một nhân vật, BỘ CHÍNH TRƯỚC (`staleRegenPlan`). Tuần tự; dừng ở lỗi thật đầu tiên.
   * Câu xác nhận nêu số ảnh + số asset AVIS vĩnh viễn (mỗi ảnh mới tự đăng ký KYC).
   */
  const regenStale = (c: FilmCharacter) => {
    const plan = staleRegenPlan(looksOf(c.id, looks));
    if (!plan.length) return;
    Modal.confirm({
      title: `Gen lại ${plan.length} bộ đồ của ${c.name}?`,
      content: <div style={{ fontSize: 13 }}>{regenConfirmText(plan.length)}<div style={{ marginTop: 6 }}>Thứ tự: {plan.map((l) => l.lookKey).join(' → ')}</div></div>,
      okText: 'Gen lại', cancelText: 'Thôi',
      onOk: async () => {
        for (const l of plan) {
          if ((await genLook(l)) === 'error') break;
        }
      },
    });
  };
  /** Ảnh ma-nơ-canh (đồ không người) — trước ở tab Wardrobe. */
  const genOutfitRef = async (look: FilmCharacterLook) => {
    setGenLookIds((s) => new Set(s).add(look.id));
    try {
      const saved = await filmApi.generateSeedanceOutfitRef(look.id);
      setLooks((list) => list.map((x) => (x.id === look.id ? { ...x, ...saved } : x)));
      message.success(`Đã gen ảnh ma-nơ-canh bộ "${look.lookKey}"`);
    } catch (e: any) { reportError(e, 'Gen ảnh ma-nơ-canh'); }
    finally { setGenLookIds((s) => { const n = new Set(s); n.delete(look.id); return n; }); }
  };
  /**
   * E8-P1 look-asset — đăng ký hoặc xoá (`lookAssetAction` quyết). Xoá = trả slot trong quota 50 dùng chung toàn tổ
   * chức ⇒ hỏi lại. Đăng ký khi chưa có ảnh người mặc ⇒ server gen ảnh trước ⇒ nói trước.
   */
  const toggleLookAsset = (look: FilmCharacterLook) => {
    const act = lookAssetAction(look);
    const go = async () => {
      setGenLookIds((s) => new Set(s).add(look.id));
      try {
        if (act.danger) {
          const r = await filmApi.releaseLookAsset(look.id);
          if (r.released) message.success('Đã xoá asset, trả slot quota về');
          else message.warning(`Không xoá được asset: ${r.error || 'không rõ'} — còn chiếm quota, xoá tay trên ModelArk`);
        } else {
          const r = await filmApi.ensureLookAsset(look.id, planId);
          message.success(r.created ? `Đã đăng ký asset ${r.assetId}${r.generatedRef ? ' (kèm một lượt gen ảnh người mặc)' : ''}` : `Asset đang dùng lại: ${r.assetId}`);
        }
        await reloadWardrobe();
      } catch (e: any) { reportError(e, 'Look asset'); }
      finally { setGenLookIds((s) => { const n = new Set(s); n.delete(look.id); return n; }); }
    };
    Modal.confirm({
      title: act.danger ? `Xoá look-asset của bộ "${look.lookKey}"?` : `Đăng ký look-asset cho bộ "${look.lookKey}"?`,
      content: act.title, okText: act.danger ? 'Xoá' : 'Đăng ký', cancelText: 'Thôi',
      okButtonProps: act.danger ? { danger: true } : undefined, onOk: go,
    });
  };
  /** Tạo bộ đồ; trùng tên thì backend trả bộ đang có ⇒ báo và KHÔNG gen (tránh tiêu tiền nhầm bộ). */
  const createLook = async (c: FilmCharacter, lookKey: string, outfit: string, gen: boolean): Promise<FilmCharacterLook | null> => {
    const existed = looks.some((l) => l.characterId === c.id && l.lookKey.toLowerCase() === lookKey.toLowerCase());
    try {
      const made = await filmApi.createLook(c.id, { lookKey, outfit: outfit || undefined });
      if (existed) { message.info(`Đã có bộ "${made.lookKey}" — chọn bộ đó, không tạo mới`); }
      setLooks((list) => (list.some((x) => x.id === made.id) ? list : [...list, made]));
      if (gen && !existed) await genLook(made);
      return made;
    } catch (e: any) { message.error(apiErrorText(e, 'Tạo bộ đồ')); return null; }
  };
  /**
   * 260927 mục N4 — ô "Dùng cho cảnh: …" của composer "Thêm bộ đồ": bộ vừa tạo/gen áp NGAY vào các cảnh
   * được chọn, không phải chờ lượt "AI gán lại" sau mới thấy đúng bộ. Tải lại `sceneRows` để "dải Theo
   * cảnh" trên thẻ + bảng gán trong Bank phản ánh ngay.
   */
  const assignScenesToLook = async (lookId: string, sceneIds: string[]) => {
    if (!planId || !sceneIds.length) return;
    try {
      await filmApi.assignLookScenes(lookId, { planId, sceneIds });
      const r = await filmApi.sceneLooks(planId).catch(() => [] as FilmSceneLook[]);
      setSceneRows(r);
    } catch (e: any) { message.error(apiErrorText(e, 'Gán bộ đồ cho cảnh')); }
  };
  const saveWardrobe = async () => {
    if (!wardrobeEditing) return;
    await patchChar(wardrobeEditing.c, { wardrobe: wardrobeEditing.value }, 'Lưu trang phục gốc');
    setWardrobeEditing(null);
  };

  // ── 260922 E2-P2: trang phục theo cảnh ──
  const reloadWardrobe = async () => {
    const [l, u, r] = await Promise.all([
      filmApi.listLooks(storyId),
      filmApi.lookUsage(storyId).catch(() => null),
      planId ? filmApi.sceneLooks(planId).catch(() => [] as FilmSceneLook[]) : Promise.resolve([] as FilmSceneLook[]),
    ]);
    setLooks(l); setLookUsage(u); setSceneRows(r);
    onChanged?.();
  };
  /** Đổi bộ cho cả đoạn: tuần tự từng dòng (≤ 7 cảnh/plan theo đo dev) — dòng nào lỗi thì báo và dừng. */
  const setRunLook = async (rowIds: string[], lookId: string) => {
    try {
      for (const id of rowIds) await filmApi.setSceneLook(id, lookId);
      message.success(`Đã đổi bộ cho ${rowIds.length} cảnh`);
    } catch (e: any) { message.error(apiErrorText(e, 'Đổi bộ đồ theo cảnh')); }
    finally { await reloadWardrobe().catch(() => undefined); }
  };
  /** Xoá bộ đồ: đang được dùng ⇒ 409 ⇒ hỏi bộ thay thế rồi gọi lại kèm `replaceWith`. */
  const deleteLook = async (look: FilmCharacterLook, replaceWith?: string) => {
    try {
      const r = await filmApi.deleteLook(look.id, replaceWith);
      message.success(`Đã xoá bộ "${r.lookKey}"${r.repointedScenes || r.rewrittenPrompts ? ` — trỏ ${r.repointedScenes} cảnh + ${r.rewrittenPrompts} prompt sang bộ thay thế` : ''}`);
      setReplacing(null);
      await reloadWardrobe();
    } catch (e: any) {
      const inUse = bankInUse(e);
      if (!inUse) { message.error(apiErrorText(e, 'Xoá bộ đồ')); return; }
      setReplacing({ look, usage: inUse.usage, name: inUse.name });
    }
  };
  const confirmDeleteLook = (look: FilmCharacterLook) => {
    // E3-P4: xoá BỘ CHÍNH ⇒ bộ khác thành bộ chính, các bộ phụ đã dựng từ ảnh bộ chính cũ không tự đổi theo (TD-FILM-101).
    const mine = looksOf(look.characterId, looks);
    const isMain = mine.length > 1 && (mine.find((l) => l.lookKey === 'default') || mine[0])?.id === look.id;
    Modal.confirm({
      title: `Xoá bộ "${look.lookKey}"?`,
      content: isMain
        ? <>Ảnh của bộ đồ này cũng mất. <b>Đây là bộ chính</b> — các bộ khác dựng từ ảnh của nó; xoá thì bộ khác sẽ thành bộ chính và các ảnh đã dựng <b>không tự đổi theo</b>.</>
        : 'Ảnh của bộ đồ này cũng mất.',
      okText: 'Xoá', okButtonProps: { danger: true }, cancelText: 'Huỷ', onOk: () => deleteLook(look),
    });
  };
  const rowsOf = (charId: string) => sceneRows.filter((r) => r.characterId === charId);
  const planScenes = plan?.scenes || [];

  const openSeedance = (c: FilmCharacter) => setSeedanceEditing(c);
  const genVoice = async (charId: string) => {
    setBusy((b) => ({ ...b, [charId]: 'voice' }));
    try {
      message.loading({ content: 'Generating voice sample (TTS)...', key: charId, duration: 0 });
      await filmApi.generateSeedanceVoiceRef(charId);
      message.success({ content: 'Voice ref generated', key: charId });
      load();
    } catch (e: any) { message.error({ content: `Error: ${e?.response?.data?.message || e?.message}`, key: charId }); }
    finally { setBusy((b) => { const n = { ...b }; delete n[charId]; return n; }); }
  };
  const shownChars = onlyIds ? chars.filter((c) => onlyIds.includes(c.id)) : filterByEpisode(chars, usage?.characterIds, { on: epOnly, filterable: !!usage?.filterable.characters, emptyMeansAll: !workflowPlanId });
  const personChars = shownChars.filter((c) => c.kind !== 'CROWD');
  const crowdChars = shownChars.filter((c) => c.kind === 'CROWD');

  // ── 260922 E1-P2: danh sách rà ngược ──
  const visibleCands = candidates.filter((c) => !dismissed.includes(c.key));
  const kindOf = (c: CastCandidate): CastKind => candKind[c.key] || c.kind;
  /** Gộp chỉ vào hồ sơ CÙNG bản chất: đám đông ↔ đám đông; người / vai lặp ↔ người / vai lặp. */
  const mergeTargets = (c: CastCandidate) => chars.filter((x) => (kindOf(c) === 'CROWD') === (x.kind === 'CROWD'));
  const addCandidate = async (c: CastCandidate) => {
    const kind = kindOf(c);
    setCandBusy(c.key);
    try {
      const made = await filmApi.createCharacter(storyId, {
        name: c.name, kind, aliases: c.aliases,
        headcount: kind === 'ROLE' ? (c.headcount || c.aliases.length || null) : null,
        role: kind === 'ROLE' ? 'supporting' : undefined,
      });
      message.success(`Đã thêm "${made.name}"${kind === 'CROWD' ? ' (đám đông)' : kind === 'ROLE' ? ' (vai lặp)' : ''}`);
      changed();
    } catch (e: any) { message.error(apiErrorText(e, 'Thêm vào Bank')); }
    finally { setCandBusy(null); }
  };
  const mergeCandidate = async (c: CastCandidate, target: FilmCharacter) => {
    const aliases = Array.from(new Set([...(target.aliases || []), c.name, ...c.aliases]))
      .filter((a) => a.trim().toLowerCase() !== target.name.trim().toLowerCase());
    setCandBusy(c.key);
    try {
      await filmApi.updateCharacter(target.id, {
        aliases,
        ...(target.kind === 'ROLE' ? { headcount: Math.max(target.headcount || 0, aliases.length) } : {}),
      });
      message.success(`Đã gộp "${c.name}" vào "${target.name}"`);
      changed();
    } catch (e: any) { message.error(apiErrorText(e, 'Gộp vào hồ sơ')); }
    finally { setCandBusy(null); }
  };
  const dismissCandidate = (c: CastCandidate) => { setDismissed(addDismissed(storyId, c.key)); onChanged?.(); };
  const saveCrowd = (c: FilmCharacter, data: { name?: string; casting?: string; headcount?: number | null }) =>
    patchChar(c, data as any, 'Lưu đám đông');
  const saveHeadcount = (c: FilmCharacter, n: number | null) => patchChar(c, { headcount: n }, 'Lưu số người');
  // Bộ plan đang mở dùng mà chưa có ảnh người mặc — CÙNG nguồn với dòng "Chuẩn bị x/3" (bankPrepUtils).
  const planChars = filterByEpisode(chars, usage?.characterIds, { on: true, filterable: !!usage?.filterable.characters, emptyMeansAll: !workflowPlanId });
  const missingDetail = looksNeedingImageDetail(planChars, looks, sceneRows);
  const missingLooks = missingDetail.looks;
  // Bộ CÓ ảnh mà chưa có asset AVIS đúng ảnh hiện tại (phạm vi "Chỉ tập này"). `failed` / `unknown` không vào — xem `needsAvisBatch`.
  const planCharIds = new Set(planChars.filter((c) => c.kind !== 'CROWD').map((c) => c.id));
  const avisMissing = looks.filter((l) => planCharIds.has(l.characterId) && l.canonicalRefUrl
    && needsAvisBatch(refAssetView(refRows.filter((r) => r.ownerId === l.id && r.provider === 'AVIS'), l.canonicalRefUrl).state));
  /** TUẦN TỰ (mỗi bộ ~6 s — không gộp một request dài bị gateway cắt ở 3 phút). Lỗi thì bỏ qua bộ đó, chạy tiếp. */
  const registerAvisMissing = async () => {
    let ok = 0;
    for (const l of avisMissing) {
      setAvisBatch(l.lookKey);
      if (await registerAvis(l, true)) ok += 1;
    }
    setAvisBatch(null);
    message.success(`Đăng ký AVIS xong ${ok}/${avisMissing.length} bộ`);
  };
  // ── 260922 E3-P3: đăng ký KYC AVIS (chỉ AVIS; BytePlus giữ nút cũ) ──
  const reloadRefs = () => { filmApi.refAssets(storyId).then(setRefRows).catch(() => undefined); };
  const AVIS_OUTCOME: Record<string, string> = {
    reused: 'đã có sẵn', registered: 'đã đăng ký', processing: 'đang xử lý phía AVIS', blocked: 'bị chặn — cần đối soát',
    failed: 'thất bại', unknown: 'không rõ kết cục — cần đối soát', skipped: 'bỏ qua', 'stale-image': 'ảnh vừa đổi',
  };
  /** Một bộ. Trả `false` khi lỗi (nút hàng loạt bỏ qua bộ đó, chạy tiếp). */
  const registerAvis = async (look: FilmCharacterLook, quiet = false): Promise<boolean> => {
    try {
      const r = await filmApi.registerLookAvis(look.id);
      const txt = `AVIS "${look.lookKey}": ${AVIS_OUTCOME[r.outcome] || r.outcome}${r.reason ? ` — ${r.reason}` : ''}`;
      if (r.outcome === 'registered' || r.outcome === 'reused') { if (!quiet) message.success(txt); }
      else if (r.outcome === 'processing') message.info(txt);
      else message.warning(txt);
      return r.outcome !== 'failed' && r.outcome !== 'unknown';
    } catch (e: any) { message.error(apiErrorText(e, `Đăng ký AVIS "${look.lookKey}"`)); return false; }
    finally { reloadRefs(); }
  };
  /** 260922 E3-P6 — ảnh nhóm của hồ sơ đám đông. Ảnh chưa vào clip (Epic 5). */
  const genCrowdGroup = async (c: FilmCharacter, promptOverride?: string) => {
    setBusy((b) => ({ ...b, [c.id]: 'group' }));
    try {
      const saved = await filmApi.generateCrowdGroupRef(c.id, planId, promptOverride);
      setChars((list) => list.map((x) => (x.id === c.id ? { ...x, ...saved } : x)));
      const a = saved.avis ? ` · AVIS: ${AVIS_OUTCOME[saved.avis.outcome] || saved.avis.outcome}` : '';
      message.success(`Đã gen ảnh nhóm "${c.name}"${a}`);
      reloadRefs();
    } catch (e: any) { reportError(e, `Gen ảnh nhóm "${c.name}"`); }
    finally { setBusy((b) => { const n = { ...b }; delete n[c.id]; return n; }); }
  };
  const registerCrowdAvis = async (c: FilmCharacter) => {
    try {
      const r = await filmApi.registerCrowdAvis(c.id);
      const txt = `AVIS ảnh nhóm "${c.name}": ${AVIS_OUTCOME[r.outcome] || r.outcome}${r.reason ? ` — ${r.reason}` : ''}`;
      if (r.outcome === 'registered' || r.outcome === 'reused') message.success(txt);
      else if (r.outcome === 'processing') message.info(txt);
      else message.warning(txt);
    } catch (e: any) { message.error(apiErrorText(e, `Đăng ký AVIS ảnh nhóm "${c.name}"`)); }
    finally { reloadRefs(); }
  };
  const reconcileAvis = async (abandon: boolean) => {
    if (!reconciling) return;
    setReconciling({ ...reconciling, busy: true });
    try {
      await filmApi.reconcileRefAsset(reconciling.rowId, abandon ? null : reconciling.assetId.trim());
      message.success(abandon ? 'Đã bỏ dòng treo — đăng ký lại được' : 'Đã cập nhật theo asset trên AVIS');
      setReconciling(null);
    } catch (e: any) { message.error(apiErrorText(e, 'Đối soát AVIS')); setReconciling({ ...reconciling, busy: false }); }
    finally { reloadRefs(); }
  };

  /**
   * Gen TUẦN TỰ theo thứ tự `looksNeedingImage` (bộ chính trước), hiện tên bộ đang chạy. DỪNG ở lỗi thật đầu tiên (thường
   * là lỗi chung: kiểm duyệt / hết quota); bộ bị server TỪ CHỐI có chủ đích thì bỏ qua, chạy tiếp.
   */
  const genMissing = async () => {
    for (const l of missingLooks) {
      setBatch(l.lookKey);
      if ((await genLook(l)) === 'error') break;
    }
    setBatch(null);
  };

  /**
   * 260927 (mục 3, review) — ảnh có sẵn trong Bank để chọn ở "＋ Ref" của ô soạn (mục A): ảnh người mặc của
   * các bộ KHÁC + ảnh bối cảnh + đạo cụ của truyện, giống cách `FilmLocationBank`/`FilmPropBank` truyền
   * `bankImages` cho tab của chúng. Loại bộ ĐANG soạn (`excludeLookId`) — không tự chọn chính mình làm ref.
   */
  const bankImagesExcluding = (excludeLookId?: string): RefImage[] => [
    ...looks.filter((l) => l.canonicalRefUrl && l.id !== excludeLookId).map((l) => ({
      key: `look-${l.id}`, url: l.canonicalRefUrl!,
      label: `${chars.find((c) => c.id === l.characterId)?.name || '?'} · ${l.lookKey} (người mặc)`,
    })),
    ...locs.filter((x) => x.canonicalRefUrl).map((x) => ({ key: `loc-${x.id}`, url: x.canonicalRefUrl!, label: `${x.name} (bối cảnh)` })),
    ...props_.filter((x) => x.canonicalRefUrl).map((x) => ({ key: `prop-${x.id}`, url: x.canonicalRefUrl!, label: `${x.name} (đạo cụ)` })),
  ];
  if(workflowPlanId&&(refError||usageError))return <div role="alert">{refError||usageError}<button type="button" onClick={()=>{load();reloadUsage();}}>Tải lại</button></div>;
  return (
    <div style={vars} className={compact ? 'ac-scope' : undefined}>
      {scoped ? null : compact ? (
        <AssetTabHead title={ASSET_TAB_HEAD.characters.title} sub={ASSET_TAB_HEAD.characters.sub} onRefresh={changed} refreshing={loading} testId="ac-head-characters">
          <EpisodeOnlyFilter board on={epOnly} onChange={setEpOnly} shown={shownChars.length} total={chars.length}
            filterable={!!usage?.filterable.characters} />
          {missingLooks.length > 0 && (
            <Popconfirm title={`Tạo ảnh cho ${missingLooks.length} bộ đồ còn thiếu?`}
              description={<div style={{ maxWidth: 320 }}>Sẽ tạo <b>{missingLooks.length} ảnh</b>{missingDetail.insertedMainIds.length > 0 && <> — gồm {missingDetail.insertedMainIds.length} bộ chính cảnh không dùng nhưng các bộ khác cần làm gốc</>}. Chạy lần lượt (bộ chính trước): {missingLooks.map((l) => l.lookKey).join(', ')}. Dừng ở lỗi đầu tiên.</div>}
              okText="Tạo" cancelText="Thôi" onConfirm={genMissing}>
              <AssetHeadButton disabled={!!batch}>{batch ? `Đang tạo: ${lookLabelOf(batch)}` : `Tạo ảnh các bộ còn thiếu · ${missingLooks.length} bộ`}</AssetHeadButton>
            </Popconfirm>
          )}
          {isSeedance && avisMissing.length > 0 && (
            <Tooltip title="Đăng ký ảnh bộ đồ lên AVIS (KYC) — để sẵn cho lúc gen clip qua AVIS (Epic 5). Bộ đăng ký lỗi thì thử lại bằng tay ở menu ⋯ của thẻ.">
              <AssetHeadButton disabled={!!avisBatch} onClick={registerAvisMissing}>
                {avisBatch ? `Đang đăng ký AVIS: ${lookLabelOf(avisBatch)}` : `Đăng ký AVIS cho ${avisMissing.length} bộ còn thiếu`}
              </AssetHeadButton>
            </Tooltip>
          )}
          {!isSeedance && <AssetHeadButton onClick={autoVoices} disabled={chars.length === 0}>Gán giọng tự động</AssetHeadButton>}
          <Tooltip title="Thêm nhân vật AI bỏ sót. Gõ tên rút gọn của người đã có (vd 'Elysia' khi Bank có 'Elysia Featherfall') thì hệ thống gộp thành alias, không tạo bản trùng.">
            <AssetAddButton onClick={() => setAdding(true)}>Thêm nhân vật</AssetAddButton>
          </Tooltip>
        </AssetTabHead>
      ) : (
      <div className="bk-head">
        <h3>Nhân vật</h3>
        <span className="sub">dùng chung cho mọi tập của truyện</span>
        <span className="sp" />
        <EpisodeOnlyFilter on={epOnly} onChange={setEpOnly} shown={shownChars.length} total={chars.length}
          filterable={!!usage?.filterable.characters} />
        {/* 260927 mục G: gỡ "Lấy nhân vật từ kịch bản" · "Lấy trang phục từ kịch bản" · "AI gán bộ đồ theo cảnh" —
            chuỗi nền `runScriptPasses` (mục E) nay tự làm cả ba việc này sau khi chạy kịch bản (Run pass), không
            cần bấm tay nữa. Endpoint backend (`promoteCast`/`extractLooks`/`proposeSceneLooks`) vẫn còn — chuỗi E dùng. */}
        {missingLooks.length > 0 && (
          <Popconfirm title={`Gen ảnh cho ${missingLooks.length} bộ đồ còn thiếu?`}
            description={<div style={{ maxWidth: 320 }}>Sẽ gen <b>{missingLooks.length} ảnh</b>{missingDetail.insertedMainIds.length > 0 && <> — gồm {missingDetail.insertedMainIds.length} bộ chính cảnh không dùng nhưng các bộ khác cần làm gốc</>}. Chạy lần lượt (bộ chính trước): {missingLooks.map((l) => l.lookKey).join(', ')}. Dừng ở lỗi đầu tiên.</div>}
            okText="Gen" cancelText="Thôi" onConfirm={genMissing}>
            <Button size="small" loading={!!batch}>{batch ? `Đang gen: ${batch}` : `Gen ảnh các bộ còn thiếu · ${missingLooks.length} bộ`}</Button>
          </Popconfirm>
        )}
        {isSeedance && avisMissing.length > 0 && (
          <Tooltip title="Đăng ký ảnh bộ đồ lên AVIS (KYC) — để sẵn cho lúc gen clip qua AVIS (Epic 5). Bộ đăng ký lỗi thì thử lại bằng tay ở menu ⋯ của thẻ.">
            <Button size="small" loading={!!avisBatch} onClick={registerAvisMissing}>
              {avisBatch ? `Đang đăng ký AVIS: ${avisBatch}` : `Đăng ký AVIS cho ${avisMissing.length} bộ còn thiếu`}
            </Button>
          </Tooltip>
        )}
        {!isSeedance && <Button size="small" onClick={autoVoices} disabled={chars.length === 0}>🔊 Auto voices</Button>}
        <Tooltip title="Thêm nhân vật AI bỏ sót. Gõ tên rút gọn của người đã có (vd 'Elysia' khi Bank có 'Elysia Featherfall') thì hệ thống gộp thành alias, không tạo bản trùng.">
          <Button size="small" type="primary" icon={<PlusOutlined />} onClick={() => setAdding(true)}>Thêm nhân vật</Button>
        </Tooltip>
        <Button size="small" icon={<ReloadOutlined />} onClick={changed} loading={loading} />
      </div>
      )}

      {!scoped && planId && (visibleCands.length > 0 || dismissed.length > 0) && (
        <div className="bk-cands">
          <div className="bk-lbl" style={{ marginBottom: 6 }}>
            Người trong kịch bản chưa có hồ sơ <span className="bk-cnt">{visibleCands.length}</span>
            <span style={{ flex: 1 }} />
            {dismissed.length > 0 && (
              <button type="button" className="bk-lnk" onClick={() => { clearDismissed(storyId); setDismissed([]); onChanged?.(); }}>
                Hiện lại {dismissed.length} mục đã bỏ qua
              </button>
            )}
          </div>
          {visibleCands.map((c) => {
            const k = kindOf(c);
            const targets = mergeTargets(c);
            return (
              <div className="bk-cand" key={c.key}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                    <Text strong style={{ fontSize: 12.5 }}>{c.name}</Text>
                    <Select size="small" className="kindsel" value={k} onChange={(v) => setCandKind((m) => ({ ...m, [c.key]: v }))}
                      options={[
                        { value: 'PERSON', label: 'Người' },
                        { value: 'ROLE', label: `Vai lặp${c.headcount ? ` ×${c.headcount}` : ''}` },
                        { value: 'CROWD', label: 'Đám đông' },
                      ]} />
                    <span className="bk-cnt">{c.events} event</span>
                    {c.aliases.length > 0 && <Text type="secondary" style={{ fontSize: 11 }}>cũng gọi: {c.aliases.join(', ')}</Text>}
                  </div>
                  {c.samples?.[0] && <div className="smp">{c.samples[0].sceneOrder ? `S${c.samples[0].sceneOrder}` : '—'} · {c.samples[0].text}</div>}
                </div>
                <div className="acts">
                  {c.mergeInto && (
                    <Button size="small" type="primary" loading={candBusy === c.key}
                      onClick={() => { const t = chars.find((x) => x.id === c.mergeInto!.id); if (t) mergeCandidate(c, t); }}>
                      Gộp vào {c.mergeInto.name}
                    </Button>
                  )}
                  <Button size="small" type={c.mergeInto ? 'default' : 'primary'} loading={candBusy === c.key} onClick={() => addCandidate(c)}>
                    {c.mergeInto ? 'Thêm riêng' : 'Thêm'}
                  </Button>
                  <Dropdown trigger={['click']} disabled={!targets.length}
                    menu={{ items: targets.map((t) => ({ key: t.id, label: t.name })), onClick: ({ key }) => { const t = chars.find((x) => x.id === key); if (t) mergeCandidate(c, t); } }}>
                    <Button size="small" disabled={!targets.length}>Gộp vào…</Button>
                  </Dropdown>
                  <Button size="small" type="text" onClick={() => dismissCandidate(c)}>Bỏ qua</Button>
                </div>
              </div>
            );
          })}
          {!visibleCands.length && <div className="bk-dimline" style={{ marginTop: 0 }}>Đã bỏ qua hết.</div>}
        </div>
      )}

      {shownChars.length === 0 ? (
        <Empty description={chars.length
          ? `Tập này không dùng nhân vật nào trong ${chars.length} nhân vật của truyện — tắt "Chỉ tập này" để xem.`
          : 'Chưa có nhân vật — nhân vật tự vào đây sau khi chạy kịch bản (nút "Run pass"); bấm "Thêm nhân vật" nếu cần thêm tay.'} />
      ) : (
        <>
        <div className={compact ? 'ac-grid' : 'bk-grid'}>
          {personChars.map((c) => (
            <FilmCharacterCard key={c.id} character={c} looks={looksOf(c.id, looks)} compact={compact} videoCount={compact ? characterVideoCountOf(usage, c.id) : undefined}
              isSeedance={isSeedance} busyAction={busy[c.id]} genLookIds={genLookIds}
              onSaveAppearance={saveAppearance} onSaveField={saveField} onSaveGender={saveGender}
              onSaveLookOutfit={saveLookOutfit} onCreateLook={createLook} onGenLook={genLook}
              imageModels={imageModels} onRegenStale={regenStale}
              onPickFace={openSeedance} onGenVoice={(x) => genVoice(x.id)} onDelete={(x) => delChar(x)}
              scenes={planScenes} sceneRows={rowsOf(c.id)} usageNote={(lookId) => lookUsageNote(lookUsage, lookId, planId)}
              onSetRunLook={setRunLook} onDeleteLook={confirmDeleteLook}
              onGenOutfitRef={genOutfitRef} onOpenOutfitStudio={setOutfitStudio} onToggleLookAsset={toggleLookAsset}
              onOpenSheetComposer={openSheetComposer} onUploadLookRef={uploadLookRef}
              onAssignScenes={assignScenesToLook}
              refAssets={refRows} onRegisterAvis={(l) => registerAvis(l)}
              onReconcileAvis={(rowId, l) => setReconciling({ rowId, label: l.lookKey, prefix: `lk-${l.id.replace(/-/g, '').slice(0, 8)}-`, assetId: '', busy: false })}
              onSaveHeadcount={saveHeadcount}
              storyCharacters={chars} onSaveIdentity={saveIdentity}
              extraMenu={[
                { key: 'wardrobe', label: 'Sửa trang phục gốc (wardrobe)', onClick: () => setWardrobeEditing({ c, value: c.wardrobe || '' }) },
                ...(!isSeedance ? [
                  { key: 'ref', label: c.canonicalRefUrl ? 'Regen canonical ref' : 'Gen canonical ref', onClick: () => run(c.id, 'ref') },
                  { key: 'element', label: 'Tạo Kling element', disabled: !c.canonicalRefUrl, onClick: () => run(c.id, 'element') },
                ] : []),
              ]}
              footer={!isSeedance ? (
                <Select size="small" style={{ width: '100%' }} placeholder="Select voice (voice_id)" value={c.voiceId || undefined}
                  onChange={(v) => setVoice(c.id, v)} allowClear options={voices.map((v) => ({ value: v.id, label: v.label }))} />
              ) : null} />
          ))}
        </div>
        {crowdChars.length > 0 && (
          <>
            {!scoped && <div className="bk-sec">Đám đông · {crowdChars.length}</div>}
            <div className={compact ? 'ac-grid' : 'bk-grid'}>
              {crowdChars.map((c) => (
                <FilmCrowdCard key={c.id} compact={compact} character={c} planMember={!!workflowPlanId&&!!usage?.characterIds.includes(c.id)} appearance={workflowPlanId&&usage?.usage?{events:usage.usage.find(r=>r.ownerId===c.id)?.eventIds.length||0,samples:(usage.usage.find(r=>r.ownerId===c.id)?.eventIds||[]).map(eventId=>({eventId,sceneOrder:null,text:usage.usage?.find(r=>r.ownerId===c.id)?.evidence.find((e:any)=>e.eventId===eventId&&e.usage==='VISIBLE')?.quote||''}))}:appearances[c.id]} busy={busy[c.id] === 'delete'}
                  onSave={saveCrowd} onDelete={(x) => delChar(x)}
                  genBusy={busy[c.id] === 'group'} onGenGroup={genCrowdGroup}
                  refAssets={refRows.filter((r) => r.ownerType === 'CROWD' && r.ownerId === c.id)}
                  onRegisterAvis={registerCrowdAvis}
                  onReconcileAvis={(rowId, x) => setReconciling({ rowId, label: `${x.name} (ảnh nhóm)`, prefix: `cr-${x.id.replace(/-/g, '').slice(0, 8)}-`, assetId: '', busy: false })} />
              ))}
            </div>
          </>
        )}
        </>
      )}

      <Modal title="Add character to Bank" open={adding} onOk={addChar} onCancel={() => setAdding(false)} okText="Add" destroyOnClose>
        <Form form={addForm} layout="vertical">
          <Form.Item name="name" label="Character name" rules={[{ required: true, message: 'Cần tên' }]}>
            <Input placeholder="vd Eleanor" />
          </Form.Item>
          <Form.Item name="role" label="Role (optional)"><Input placeholder="vd Mẹ kế" /></Form.Item>
          <Form.Item name="gender" label="Gender">
            <Select allowClear placeholder="— unknown —" options={GENDER_OPTIONS} />
          </Form.Item>
          <Form.Item name="casting" label="Casting / appearance (optional)" extra="Nên ghi rõ giới và tuổi — đó là thứ engine dùng để chọn giọng khi nhân vật kể chuyện.">
            <Input.TextArea rows={2} placeholder="vd Caucasian woman, late 40s, sắc lạnh, tóc vàng cuốn cao" />
          </Form.Item>
        </Form>
      </Modal>
      <Modal title={`"${replacing?.name || ''}" đang được dùng`} open={!!replacing} onCancel={() => setReplacing(null)}
        okText="Đổi sang bộ này rồi xoá" cancelText="Huỷ" okButtonProps={{ danger: true, disabled: !replacing?.to }}
        onOk={() => replacing?.to && deleteLook(replacing.look, replacing.to)} destroyOnClose>
        <div style={{ fontSize: 13, marginBottom: 12 }}>
          {replacing?.usage.map((u) => <div key={u}>• {u}</div>)}
        </div>
        {(() => {
          if (!replacing) return null;
          const alts = looks.filter((l) => l.characterId === replacing.look.characterId && l.id !== replacing.look.id);
          if (!alts.length) {
            return <Paragraph type="warning" style={{ margin: 0 }}>Nhân vật chưa có bộ nào khác để thay. Tạo bộ khác trước rồi mới xoá được.</Paragraph>;
          }
          return (<>
            <Paragraph type="secondary" style={{ fontSize: 12 }}>
              Chọn bộ thay thế — mọi cảnh và chip trong prompt đang dùng bộ này sẽ trỏ sang bộ đó, rồi mới xoá.
            </Paragraph>
            <Select style={{ width: '100%' }} placeholder="Bộ thay thế" value={replacing.to}
              onChange={(v) => setReplacing((r) => (r ? { ...r, to: v } : r))}
              options={alts.map((l) => ({ value: l.id, label: l.lookKey }))} />
          </>);
        })()}
      </Modal>

      {/* 260922 E3-P3 — đối soát dòng đăng ký AVIS không rõ kết cục (có thể đã tạo asset; AVIS không xoá được ⇒ không tự tạo lại). */}
      <Modal title={`Đối soát AVIS: ${reconciling?.label || ''}`} open={!!reconciling} onCancel={() => setReconciling(null)}
        footer={[
          <Button key="cancel" onClick={() => setReconciling(null)}>Đóng</Button>,
          <Popconfirm key="abandon" title="Bỏ dòng này?" description="Chỉ bỏ khi chắc AVIS KHÔNG có asset nào tên như dưới — nếu có, lần đăng ký sau sẽ tạo thêm một asset (không xoá được)."
            okText="Bỏ" cancelText="Thôi" onConfirm={() => reconcileAvis(true)}>
            <Button danger loading={reconciling?.busy}>Không có trên AVIS — bỏ dòng</Button>
          </Popconfirm>,
          <Button key="ok" type="primary" loading={reconciling?.busy} disabled={!reconciling?.assetId.trim()} onClick={() => reconcileAvis(false)}>Cập nhật theo asset id</Button>,
        ]}>
        <Paragraph style={{ fontSize: 13 }}>
          Lượt đăng ký trước không rõ đã tạo asset hay chưa. Tìm trên AVIS asset có tên bắt đầu bằng <Text code>{reconciling?.prefix}</Text>:
          thấy thì dán asset id vào dưới; không thấy thì bỏ dòng để đăng ký lại.
        </Paragraph>
        <Input placeholder="asset-2026…" value={reconciling?.assetId || ''} onChange={(e) => reconciling && setReconciling({ ...reconciling, assetId: e.target.value })} />
      </Modal>

      {outfitStudio && (
        <ImageComposer open={!!outfitStudio} backend={{ kind: 'studio', ownerType: 'LOOK', ownerId: outfitStudio.id }} planId={planId}
          title={`${chars.find((x) => x.id === outfitStudio.characterId)?.name || ''} · ${outfitStudio.lookKey} (ma-nơ-canh — đường BytePlus)`}
          bankImages={bankImagesExcluding(outfitStudio.id)}
          onClose={() => setOutfitStudio(null)} onChanged={() => { reloadWardrobe().catch(() => undefined); }} />
      )}

      {sheetComposer && (
        <ImageComposer
          open={!!sheetComposer}
          backend={{ kind: 'lookSheet', lookId: sheetComposer.look.id, sheetModels: sheetModelOptions(imageModels, sheetComposer.look.sheetInput), allModels: imageModels, sheetInput: sheetComposer.look.sheetInput }}
          planId={planId}
          title={`${chars.find((x) => x.id === sheetComposer.look.characterId)?.name || ''} · ${sheetComposer.look.lookKey} (ảnh người mặc)`}
          lockedRef={sheetComposer.lockedRef}
          bankImages={bankImagesExcluding(sheetComposer.look.id)}
          onClose={() => setSheetComposer(null)}
          onChanged={() => { filmApi.listLooks(storyId).then(setLooks).catch(() => undefined); reloadRefs(); onChanged?.(); }}
        />
      )}

      <Modal title={`Trang phục gốc: ${wardrobeEditing?.c.name || ''}`} open={!!wardrobeEditing} onOk={saveWardrobe}
        onCancel={() => setWardrobeEditing(null)} okText="Lưu" cancelText="Huỷ" destroyOnClose>
        <Paragraph type="secondary" style={{ fontSize: 12 }}>
          Trang phục AI khai cho nhân vật — chỉ dùng để tạo bộ <Text code>default</Text> khi nhân vật chưa có bộ nào.
          Muốn đổi ảnh bộ đồ đang có thì sửa dòng <b>"Bộ đang chọn"</b> trên thẻ.
        </Paragraph>
        <Input.TextArea rows={3} value={wardrobeEditing?.value}
          onChange={(e) => setWardrobeEditing((w) => (w ? { ...w, value: e.target.value } : w))} />
      </Modal>

      <FaceSourceModal character={seedanceEditing} onClose={() => setSeedanceEditing(null)} onSaved={changed} planId={planId} />
    </div>
  );
};

export default FilmReferenceTab;